// The access credential, read from the URL fragment.
//
// The fragment is never transmitted to a server by a browser, so it appears in
// no Cloudflare request log and in no Referer header — unlike a query string,
// which the hosting platform logs on every request.
//
// The page deliberately does NOT judge whether the credential is correct. To do
// that it would need the expected value inside a static asset, served before any
// function can run, and `curl .../app.js` would hand it over. The page forwards
// whatever it was given; only the function knows the expected value.
//
// Absence, though, is detectable without knowing anything — so the page can say
// "this link is incomplete" before she fills in four steps.

export const CREDENTIAL_HEADER = 'x-date-night-key';

/**
 * @returns {string | null} the credential, or null when the link carries none
 *
 * Only a fragment is accepted. A non-empty argument that does not begin with #
 * is refused outright — notably a query string, which `URLSearchParams` would
 * otherwise parse quite happily. That would mean the credential arriving by a
 * route the platform logs, so the reader refuses rather than silently obliging.
 */
export function readCredential(hash = globalThis.location?.hash ?? '') {
  if (typeof hash !== 'string' || hash.length === 0) return null;
  if (!hash.startsWith('#')) return null;
  const raw = hash.slice(1);
  if (!raw) return null;
  const value = new URLSearchParams(raw).get('k');
  return value && value.length > 0 ? value : null;
}

export function hasCredential(hash) {
  return readCredential(hash) !== null;
}

/**
 * The event this visit is replacing, if she arrived from a "Change the date"
 * button. Same fragment as the credential, so it never reaches a server log.
 *
 * @returns {string | null}
 */
export function readReplaceEventId(hash = globalThis.location?.hash ?? '') {
  if (typeof hash !== 'string' || !hash.startsWith('#')) return null;
  const value = new URLSearchParams(hash.slice(1)).get('replace');
  return value && /^\d{5,25}$/.test(value) ? value : null;
}
